import {useMemo} from 'react'

import {
  PriceTrendsParams,
  useGetPriceTrendsQuery
} from '../services/priceInsightsApi'
import {PriceTrendInsightsPayload} from '../types'
import {
  isEligibleForPriceInsights,
  isInsightsDataValid
} from '../utils/validation'

interface UsePriceTrendInsightsParams extends Partial<PriceTrendsParams> {
  enabled?: boolean
}

interface UsePriceTrendInsightsReturn {
  insights: PriceTrendInsightsPayload | undefined
  data: ReturnType<typeof useGetPriceTrendsQuery>['data']
  isLoading: boolean
  isError: boolean
  error: unknown
}
/**
 * Hook to fetch price trend insights from the API.
 * Validates that insights have at least 7 days of data before returning them.
 *
 * @param params - Parameters for fetching price trends (hotelId, checkIn, checkOut, rooms, currency)
 * @returns Price trend insights payload with loading/error states
 */
export const usePriceTrendInsights = (
  params?: UsePriceTrendInsightsParams
): UsePriceTrendInsightsReturn => {
  const {hotelId, checkIn, checkOut, rooms, currency} = params || {}

  const hasAllParams =
    hotelId && checkIn && checkOut && rooms !== undefined && currency

  // Only fetch if eligible: single room with no children
  const isEligible = isEligibleForPriceInsights(rooms)

  const {data, isLoading, isError, error} = useGetPriceTrendsQuery(
    params as PriceTrendsParams,
    {
      skip: !hasAllParams || !isEligible
    }
  )

  // Transform API response to PriceTrendInsightsPayload format
  // Only return insights if they meet the minimum data requirements (≥7 days)
  const insights: PriceTrendInsightsPayload | undefined = useMemo(() => {
    if (!data) return undefined

    const transformedInsights: PriceTrendInsightsPayload = {
      medianHotelPrice: data.medianHotelPrice,
      timeRangeDays: data.timeRangeDays,
      lastMedianSimilarHotelsPricePoint: data.lastMedianSimilarHotelsPricePoint,
      hotelChangePercentageThreshold: data.hotelChangePercentageThreshold,
      marketChangePercentageThreshold: data.marketChangePercentageThreshold,
      marketPriceTrendPercentage: data.marketPriceTrendPercentage
    }

    // Validate insights before returning - require at least MIN_DAYS_FOR_INSIGHTS days of data
    if (!isInsightsDataValid(transformedInsights)) {
      return undefined
    }

    return transformedInsights
  }, [data])

  return {
    insights,
    data,
    isLoading,
    isError,
    error
  }
}
